/** Bounded source-socket cable and cannon tells. The domain supplies every
 * endpoint/knot; this owner has no contact, wrap, health or repair policy.
 * Reduced motion retains the cable and precision tell, removing only pulses. */
export function createSkyTwinTether(T, scene, own, reduced = () => false) {
  const geometry = own(new T.BufferGeometry());
  const positions = new T.Float32BufferAttribute(new Float32Array(8 * 3), 3);
  const colors = new T.Float32BufferAttribute(new Float32Array(8 * 3), 3);
  positions.setUsage(T.DynamicDrawUsage);
  colors.setUsage(T.DynamicDrawUsage);
  geometry.setAttribute('position', positions);
  geometry.setAttribute('color', colors);
  const material = own(
    new T.LineBasicMaterial({
      vertexColors: true,
      transparent: true,
      opacity: 0.95,
      depthWrite: false,
      toneMapped: false,
    }),
  );
  const lines = new T.LineSegments(geometry, material);
  lines.name = 'Executioners actual tether and source cannon tells';
  lines.frustumCulled = false;
  lines.visible = false;
  scene.add(lines);
  const dotsGeometry = own(new T.BufferGeometry());
  const dots = new T.Float32BufferAttribute(new Float32Array(24 * 3), 3);
  dots.setUsage(T.DynamicDrawUsage);
  dotsGeometry.setAttribute('position', dots);
  const dotMaterial = own(
    new T.PointsMaterial({
      color: '#fff0c4',
      size: 0.32,
      sizeAttenuation: true,
      transparent: true,
      opacity: 0.8,
      depthWrite: false,
      toneMapped: false,
    }),
  );
  const points = new T.Points(dotsGeometry, dotMaterial);
  points.name = 'Executioners cable power direction';
  points.frustumCulled = false;
  points.visible = false;
  scene.add(points);
  let vertices = 0,
    particles = 0;
  function segment(a, b, color) {
    if (!a || !b || vertices + 2 > 8) return;
    for (const p of [a, b]) {
      positions.setXYZ(vertices, p.x, p.y, p.z);
      colors.setXYZ(vertices, ...color);
      vertices++;
    }
  }
  return {
    draw(state, visible = true) {
      vertices = particles = 0;
      const t = state.boss?.twins,
        cable = t?.tether;
      if (visible && t && !state.boss.defeated) {
        if (cable?.from && cable.to && !cable.broken) {
          const path = [cable.from, ...(cable.knot ? [cable.knot] : []), cable.to];
          const color = [1, 0.8 - (cable.tension ?? 0) * 0.55, 0.32 - (cable.tension ?? 0) * 0.2];
          for (let i = 1; i < path.length; i++) {
            segment(path[i - 1], path[i], color);
            if (!reduced())
              for (let j = 0; j < 10; j++) {
                const q = (j / 10 + ((t.clock * 0.45) % 1)) % 1,
                  a = path[i - 1],
                  b = path[i];
                dots.setXYZ(
                  particles++,
                  a.x + (b.x - a.x) * q,
                  a.y + (b.y - a.y) * q,
                  a.z + (b.z - a.z) * q,
                );
              }
          }
        }
        if (t.repairLink) {
          const { from, to, progress } = t.repairLink;
          segment(from, to, [0.2, 0.6 + progress * 0.4, 1]);
          if (!reduced())
            for (let j = 0; j < Math.floor(progress * 10) && particles < 24; j++) {
              const q = (j / 10 + ((t.clock * 0.35) % 1)) % 1;
              dots.setXYZ(
                particles++,
                from.x + (to.x - from.x) * q,
                from.y + (to.y - from.y) * q,
                from.z + (to.z - from.z) * q,
              );
            }
        }
        for (const tell of t.tells ?? [])
          segment(tell.from, tell.to, tell.precision ? [1, 0.2, 0.28] : [0.7, 0.75, 1]);
      }
      lines.visible = vertices > 0;
      points.visible = particles > 0;
      geometry.setDrawRange(0, vertices);
      dotsGeometry.setDrawRange(0, particles);
      if (vertices) {
        positions.needsUpdate = true;
        colors.needsUpdate = true;
      }
      if (particles) dots.needsUpdate = true;
    },
    diagnostics() {
      return { segments: vertices / 2, particles, maximumSegments: 4, maximumParticles: 24 };
    },
  };
}
